import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../environments/environment';
import { ShippingConfig } from '../_models/shipping-config.interface';

export interface OperationRequirement {
  id: number;
  operation_id: number;
  article_template_id?: number;
  article_template_description?: string;
  free_text?: string;
  quantity: number;
  action_type: 'STAGING' | 'VERSAND';
  /** Bereits ueber archivierte Lieferscheine versandte Menge (nur fuer
   * VERSAND relevant). Quantity darf nicht darunter fallen. */
  already_shipped_quantity?: number;
}

/** Versand-Status einer STAGING-Anforderung: bereits versandte (diesem
 * Einbau-Auftrag zugeordnete) Staging-Einheiten je Anforderung. */
export interface StagingSummaryEntry {
  operation_requirement_id: number;
  article_template_id?: number;
  article_template_description?: string;
  target: number;
  shipped: number;
  /** Zurueckgehalten (verzoegerter Versand): vorbereitet, aber noch nicht
   * physisch versandt (ship_ready_at gesetzt, shipped_at nil). */
  delayed: number;
  open: number;
}

// ----- Excel-Bulk-Edit Interfaces -------------------------------------------

export type ExcelTemplateSource = 'project' | 'customer';

export interface ExcelChange {
  operation_id: number;
  kind: 'create' | 'update' | 'delete';
  template_id?: number | null;
  free_text?: string | null;
  old_quantity: number;
  new_quantity: number;
  action_type: 'VERSAND' | 'STAGING';
  error?: string | null;
}

export interface ExcelPreviewColumn {
  id: string;                    // 't:<template_id>' oder 'f:<text>'
  kind: 'template' | 'free_text';
  template_id?: number;
  free_text?: string;
  label: string;                 // Anzeigename in der Diff-Tabelle
  is_staging: boolean;           // wenn Template als Staging-Vorlage im Projekt
}

export interface ExcelPreviewCell {
  old_quantity: number;
  new_quantity: number;
  delta: number;
  has_change: boolean;
  /** True wenn der Wert direkt aus dem Description-Parsing uebernommen wurde
   * (existing == nil und new_quantity == parsed_quantity). Frontend
   * kennzeichnet diese Cells mit blauem Parsing-Hinweis statt grünem +X. */
  is_parsing_takeover?: boolean;
  has_error: boolean;
  error?: string | null;
  action_type: 'VERSAND' | 'STAGING';
}

export interface ExcelPreviewRow {
  operation_id: number;
  ticket_id?: number;
  order_nr?: string;
  external_order_nr?: string;
  operation_date?: string;
  operation_date_iso?: string;
  store_name?: string;
  technician_name?: string;
  cells: Record<string, ExcelPreviewCell>;
  /** Mapping operation_requirement_id → Spalten-Token (col_id) + action_type.
   *  Das Inline-Split-Grid mappt damit requirement_split/requirement_committed
   *  der Configs (nach operation_requirement_id gekeyt) auf die Artikel-Spalten. */
  requirements?: Array<{ id: number; action_type: 'VERSAND' | 'STAGING'; col_id: string }>;
  has_change: boolean;
  has_error: boolean;
  /** Plural (Multi-Config): alle noch nicht vollstaendig abgeschlossenen Configs
   *  der Operation, je mit `requirement_split`. Quelle fuer die Pro-Config-
   *  Zeilen in der Versandkonfig-Spalte. */
  shipping_configs?: ShippingConfig[];
  /** Vollstaendig abgeschlossene Configs (beide Kanaele versandt) — read-only
   *  Zeilen im Inline-Split-Grid. Ihre `requirement_committed`-Mengen werden
   *  angezeigt und aus dem offenen Pool herausgerechnet. */
  completed_shipping_configs?: ShippingConfig[];
  /** Form-Meta — wird der ShippingConfigFormComponent als Inputs uebergeben,
   * damit kein separater API-Call pro Cell-Click noetig ist. */
  ticket_store_id?: number;
  ticket_lat?: number;
  ticket_lng?: number;
  project_id?: number;
  operation_technician_id?: number;
  /** Klassifizierungs-Felder fuer den Auto-Create-Workflow. is_internal kommt
   * vom [INTERN]-Praefix im Projektnamen; priority_material_shipping spiegelt
   * das Flag auf der Ticket-Priority. Has-Staging-Artikel wird im Frontend
   * aus den cells abgeleitet (action_type === 'STAGING' && new_quantity > 0). */
  is_internal?: boolean;
  priority_material_shipping?: boolean;
}

export interface ExcelPreviewResult {
  success: boolean;
  errors: string[];
  row_errors: { row: number; error: string }[];
  operation_ids: number[];
  changes: ExcelChange[];
  preview_columns: ExcelPreviewColumn[];
  preview_rows: ExcelPreviewRow[];
}

export interface ExcelApplyResult {
  success: boolean;
  created: number;
  updated: number;
  deleted: number;
  errors: string[];
  /** Atomarer „Zeile pro Config"-Pfad: separat gezaehlte Config-Anlagen/-Updates
   *  (created/updated zaehlen nur Requirements). config_ids mappt Temp-Refs auf
   *  die frisch vergebenen IDs. */
  config_created?: number;
  config_updated?: number;
  config_ids?: Record<string, number>;
}

/** Eine Config-Spec fuer den atomaren Apply: neue Config (mit `ref`) oder
 *  Update einer bestehenden (`config_id`). Restliche Felder = Form-Daten. */
export interface ExcelApplyConfigSpec {
  ref?: string;
  operation_id: number;
  config_id?: number;
  [key: string]: unknown;
}

/** Eine Split-Gruppe je Config (Replace-Semantik; leere requirements leeren). */
export interface ExcelApplySplitGroup {
  operation_id: number;
  config_ref: string;
  requirements: Array<{ action_type: 'VERSAND' | 'STAGING'; template_id?: number; free_text?: string; quantity: number }>;
}

@Injectable({
  providedIn: 'root',
})
export class OperationRequirementService {
  private readonly basePath = environment.apiURL;
  private readonly excelBasePath = environment.apiURL + 'operation_requirements/excel';

  constructor(private http: HttpClient) {}

  async loadRequirements(operationId: number): Promise<OperationRequirement[]> {
    try {
      const result = await firstValueFrom(
        this.http.get<{ requirements: OperationRequirement[] }>(
          this.basePath + `operations/${operationId}/requirements.json`,
          { withCredentials: true }
        )
      );
      return result.requirements;
    } catch (err) {
      console.error('Fehler beim Laden der Requirements:', err);
      return [];
    }
  }

  /** Lädt den Staging-Versand-Status (Soll/Versandt/Offen je STAGING-Anforderung)
   *  für einen Einbau-Auftrag. */
  async loadStagingSummary(operationId: number): Promise<StagingSummaryEntry[]> {
    try {
      const result = await firstValueFrom(
        this.http.get<{ staging: { per_requirement: StagingSummaryEntry[] } }>(
          this.basePath + `operations/${operationId}/shipping_summary.json`,
          { withCredentials: true }
        )
      );
      return result.staging?.per_requirement ?? [];
    } catch (err) {
      console.error('Fehler beim Laden der Staging-Versand-Übersicht:', err);
      return [];
    }
  }

  async createRequirement(operationId: number, data: Partial<OperationRequirement>): Promise<OperationRequirement | null> {
    try {
      const result = await firstValueFrom(
        this.http.post<{ requirement: OperationRequirement }>(
          this.basePath + `operations/${operationId}/requirements.json`,
          data,
          { withCredentials: true }
        )
      );
      return result.requirement;
    } catch (err) {
      console.error('Fehler beim Erstellen des Requirements:', err);
      return null;
    }
  }

  async updateRequirement(id: number, data: Partial<OperationRequirement>): Promise<OperationRequirement | null> {
    try {
      const result = await firstValueFrom(
        this.http.patch<{ requirement: OperationRequirement }>(
          this.basePath + `operation_requirements/${id}.json`,
          data,
          { withCredentials: true }
        )
      );
      return result.requirement;
    } catch (err) {
      console.error('Fehler beim Aktualisieren des Requirements:', err);
      return null;
    }
  }

  async deleteRequirement(id: number): Promise<boolean> {
    try {
      await firstValueFrom(
        this.http.delete(
          this.basePath + `operation_requirements/${id}.json`,
          { withCredentials: true }
        )
      );
      return true;
    } catch (err) {
      console.error('Fehler beim Löschen des Requirements:', err);
      return false;
    }
  }

  // Legt Versand-Requirements aus dem Description-Parsing an. Backend
  // blockt, wenn schon Versand-Requirements existieren — der Aufrufer
  // sollte den Button nur zeigen, wenn versandRequirements leer ist.
  async parseFromDescription(operationId: number): Promise<{ requirements: OperationRequirement[]; createdCount: number } | { error: string }> {
    try {
      const result = await firstValueFrom(
        this.http.post<{ requirements: OperationRequirement[]; created_count: number }>(
          this.basePath + `operations/${operationId}/requirements/parse_from_description.json`,
          {},
          { withCredentials: true }
        )
      );
      return { requirements: result.requirements, createdCount: result.created_count };
    } catch (err: any) {
      console.error('Fehler beim Parsing der Auftragsbeschreibung:', err);
      return { error: err?.error?.error || 'Parsing fehlgeschlagen' };
    }
  }

  // ---------------------------------------------------------------------------
  // Excel-Bulk-Edit-Workflow fuer Versand-/Staging-Artikel ueber mehrere
  // Operations: Export → Bearbeiten → Preview → Apply.
  // ---------------------------------------------------------------------------

  /** Triggert den Excel-Download via Blob-Response (Browser speichert die Datei). */
  async exportExcel(operationIds: number[], projectId: number, templateSource: ExcelTemplateSource): Promise<Blob> {
    return firstValueFrom(
      this.http.post(`${this.excelBasePath}/export`, {
        operation_ids: operationIds,
        project_id: projectId,
        template_source: templateSource,
      }, {
        responseType: 'blob',
        withCredentials: true,
      }),
    );
  }

  async importExcelPreview(file: File): Promise<ExcelPreviewResult> {
    const formData = new FormData();
    formData.append('file', file);
    return firstValueFrom(
      this.http.post<ExcelPreviewResult>(`${this.excelBasePath}/import_preview`, formData, {
        withCredentials: true,
      }),
    );
  }

  async importExcelApply(changes: ExcelChange[]): Promise<ExcelApplyResult> {
    return firstValueFrom(
      this.http.post<ExcelApplyResult>(`${this.excelBasePath}/import_apply`, { changes }, {
        withCredentials: true,
      }),
    );
  }

  /** Atomarer „Zeile pro Config"-Apply: Mengen-Diffs, Versandkonfigurationen und
   *  der Mengen-Split gehen in EINEM Request raus und werden serverseitig in
   *  EINER Transaction angewandt. */
  async importExcelApplyAtomic(
    changes: ExcelChange[],
    configs: ExcelApplyConfigSpec[],
    split: ExcelApplySplitGroup[],
  ): Promise<ExcelApplyResult> {
    return firstValueFrom(
      this.http.post<ExcelApplyResult>(`${this.excelBasePath}/import_apply`, { changes, configs, split }, {
        withCredentials: true,
      }),
    );
  }
}
